import { useState, useMemo } from "react";
import { useStock } from "../App";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const Icons = {
  Revenue: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>,
  Profit: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>,
  Loss: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline><polyline points="17 18 23 18 23 12"></polyline></svg>,
  Net: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>,
  Filter: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>,
  ChevronLeft: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>,
  ChevronRight: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
};

export default function ReportsPage() {
  const { sales } = useStock();

  // Pagination & Filter States
  const [selectedMonth, setSelectedMonth] = useState("All Time");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Complex calculations memoized for performance
  const { summaryCards, finalChartData, availableMonths, monthlyData, paginatedProducts, totalPages } = useMemo(() => {
    let totalRevenue = 0, totalCOGS = 0, totalLoss = 0, totalExpense = 0; 
    
    const monthMap = {}; 
    const allTimeProductStats = {};
    const monthProductStats = {}; 
    const monthSet = new Set();

    // Helper to init product stat structure
    const initProdStat = (name) => ({ name, soldQty: 0, revenue: 0, cogs: 0, profit: 0, lossQty: 0, lossAmount: 0 });

    sales.forEach(s => {
      const d = new Date(s.date);
      const mKey = isNaN(d) ? "Unknown" : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const sortKey = isNaN(d) ? 0 : new Date(d.getFullYear(), d.getMonth(), 1).getTime();

      if (mKey !== "Unknown") monthSet.add(mKey);

      if (!monthMap[mKey]) monthMap[mKey] = { month: mKey, revenue: 0, profit: 0, loss: 0, expense: 0, sortKey };
      if (!monthProductStats[mKey]) monthProductStats[mKey] = {};

      if (s.status === "Completed") {
        const rev = parseFloat(s.amountNum || 0);
        totalRevenue += rev;
        monthMap[mKey].revenue += rev;
        
        let dailyCogs = 0;
        if (s.receiptItems) {
          s.receiptItems.forEach(item => {
            const cost = parseFloat(item.unitCost || 0);
            const qty = parseFloat(item.qty || 0);
            const itemRev = parseFloat(item.total || 0);
            const cogs = cost * qty;
            const profit = itemRev - cogs;

            totalCOGS += cogs;
            dailyCogs += cogs;

            // All Time Stats
            if (!allTimeProductStats[item.name]) allTimeProductStats[item.name] = initProdStat(item.name);
            allTimeProductStats[item.name].soldQty += qty;
            allTimeProductStats[item.name].revenue += itemRev;
            allTimeProductStats[item.name].profit += profit;

            // Monthly Stats
            if (!monthProductStats[mKey][item.name]) monthProductStats[mKey][item.name] = initProdStat(item.name);
            monthProductStats[mKey][item.name].soldQty += qty;
            monthProductStats[mKey][item.name].revenue += itemRev;
            monthProductStats[mKey][item.name].profit += profit;
          });
        }
        monthMap[mKey].profit += (rev - dailyCogs);
      } 
      else if (s.status === "Loss") {
        const lCost = parseFloat(s.lossCost || 0);
        totalLoss += lCost;
        monthMap[mKey].loss += lCost;
        monthMap[mKey].profit -= lCost;

        let pName = "Unknown Product";
        let lQty = 0;
        if (s.itemsSummary) {
          const match = s.itemsSummary.match(/(.+)\s\(x([\d.]+)\)/);
          if (match) { pName = match[1]; lQty = parseFloat(match[2]); } 
          else { pName = s.itemsSummary; }
        }

        // All Time
        if (!allTimeProductStats[pName]) allTimeProductStats[pName] = initProdStat(pName);
        allTimeProductStats[pName].lossQty += lQty;
        allTimeProductStats[pName].lossAmount += lCost;

        // Monthly
        if (!monthProductStats[mKey][pName]) monthProductStats[mKey][pName] = initProdStat(pName);
        monthProductStats[mKey][pName].lossQty += lQty;
        monthProductStats[mKey][pName].lossAmount += lCost;
      }
      else if (s.status === "Expense") {
        const expVal = parseFloat(s.amountNum || 0);
        totalExpense += expVal;
        monthMap[mKey].expense += expVal;
        monthMap[mKey].profit -= expVal;
      }
    });

    const grossProfit = totalRevenue - totalCOGS;
    const netProfit = grossProfit - totalLoss - totalExpense; 

    const cards = [
      { label: "Total Revenue", value: `¥${totalRevenue.toLocaleString(undefined, {minimumFractionDigits: 2})}`, sub: "Total sales amount", icon: Icons.Revenue, color: "#6366f1", bg: "#eef2ff", border: "#c7d2fe" },
      { label: "Gross Profit",  value: `¥${grossProfit.toLocaleString(undefined, {minimumFractionDigits: 2})}`,  sub: "Sell Price - Cost Price", icon: Icons.Profit, color: "#10b981", bg: "#d1fae5", border: "#a7f3d0" },
      { label: "Total Expenses",value: `¥${(totalLoss + totalExpense).toLocaleString(undefined, {minimumFractionDigits: 2})}`, sub: "Loss + Op. Expenses", icon: Icons.Loss, color: "#ef4444", bg: "#fee2e2", border: "#fecaca" },
      { label: "Net Profit",    value: `¥${netProfit.toLocaleString(undefined, {minimumFractionDigits: 2})}`,    sub: "Final Profit after costs", icon: Icons.Net, color: "#0f172a", bg: "#f1f5f9", border: "#cbd5e1" },
    ];

    const mData = Object.values(monthMap).sort((a,b) => a.sortKey - b.sortKey);
    const chartData = mData.length > 0 ? mData : [{ month: "No Data", revenue: 0, profit: 0, loss: 0, expense: 0 }];
    const aMonths = Array.from(monthSet).sort((a,b) => new Date(a) - new Date(b));

    // Resolve which data to show in Product Table
    const activeProductStats = selectedMonth === "All Time" ? allTimeProductStats : (monthProductStats[selectedMonth] || {});
    const sortedProducts = Object.values(activeProductStats).sort((a, b) => b.profit - a.profit);
    
    // Pagination logic
    const totalPgs = Math.ceil(sortedProducts.length / itemsPerPage) || 1;
    // Ensure currentPage is valid if filter reduces total pages
    const validPage = Math.min(currentPage, totalPgs);
    const paginated = sortedProducts.slice((validPage - 1) * itemsPerPage, validPage * itemsPerPage);

    return { summaryCards: cards, finalChartData: chartData, availableMonths: aMonths, monthlyData: mData, paginatedProducts: paginated, totalPages: totalPgs };
  }, [sales, selectedMonth, currentPage]);

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  const cardStyle = { background: "#FFFFFF", borderRadius: 16, border: "1px solid #e2e8f0", padding: "24px", boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, paddingBottom: 20 }}>
      
      {/* 1. TOP CARDS */}
      <div className="grid-4">
        {summaryCards.map((s, i) => (
          <div key={i} style={{ ...cardStyle, padding: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
              <span style={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>{s.label}</span>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: s.bg, border: `1px solid ${s.border}`, color: s.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {s.icon}
              </div>
            </div>
            <p style={{ fontSize: 26, fontWeight: 800, color: s.label === "Total Expenses" ? "#b91c1c" : "#0f172a", margin: 0, fontFamily: "system-ui, sans-serif", letterSpacing: "-0.5px" }}>{s.value}</p>
            <p style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500, margin: "6px 0 0" }}>{s.sub}</p>
          </div>
        ))}
      </div>

      {/* 2. MONTHLY GRAPH */}
      <div style={{ ...cardStyle, paddingBottom: "16px" }}>
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", margin: "0 0 4px 0" }}>Monthly Performance</h3>
          <p style={{ fontSize: 13, color: "#94a3b8", margin: 0 }}>Tracking Revenue, Net Profit, and Expenses month over month</p>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={finalChartData}>
            <defs>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} /><stop offset="95%" stopColor="#6366f1" stopOpacity={0} /></linearGradient>
              <linearGradient id="prof" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.2} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
              <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ef4444" stopOpacity={0.15} /><stop offset="95%" stopColor="#ef4444" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 600 }} axisLine={false} tickLine={false} dy={10} />
            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} dx={-10} tickFormatter={val => `¥${val}`} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)", fontSize: 12, fontWeight: 600 }} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 10, fontWeight: 500 }} />
            
            <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#6366f1" fill="url(#rev)" strokeWidth={3} />
            <Area type="monotone" dataKey="profit" name="Net Profit" stroke="#10b981" fill="url(#prof)" strokeWidth={3} />
            <Area type="monotone" dataKey="expense" name="Op. Expenses" stroke="#ef4444" fill="url(#exp)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* 3. MONTHLY BREAKDOWN TABLE */}
      <div style={cardStyle}>
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", margin: 0 }}>Monthly Financial Breakdown</h3>
          <p style={{ fontSize: 13, color: "#94a3b8", margin: "4px 0 0" }}>Clear view of your expenses, losses, and profit per month</p>
        </div>
        <div className="table-responsive">
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                {["Month", "Revenue", "Op. Expenses", "Loss Amount", "Net Profit"].map((h) => (
                  <th key={h} style={{ textAlign: "left", fontSize: 11, color: "#64748b", fontWeight: 600, padding: "0 8px 12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {monthlyData.length > 0 ? monthlyData.map((m, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f8fafc", transition: "background 0.2s" }} onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "16px 8px", fontSize: 13, color: "#0f172a", fontWeight: 700 }}>{m.month}</td>
                  <td style={{ padding: "16px 8px", fontSize: 13, color: "#6366f1", fontWeight: 700 }}>¥{m.revenue.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                  <td style={{ padding: "16px 8px", fontSize: 13, color: m.expense > 0 ? "#f59e0b" : "#94a3b8", fontWeight: 600 }}>{m.expense > 0 ? `¥${m.expense.toLocaleString(undefined, {minimumFractionDigits: 2})}` : "-"}</td>
                  <td style={{ padding: "16px 8px", fontSize: 13, color: m.loss > 0 ? "#ef4444" : "#94a3b8", fontWeight: 600 }}>{m.loss > 0 ? `¥${m.loss.toLocaleString(undefined, {minimumFractionDigits: 2})}` : "-"}</td>
                  <td style={{ padding: "16px 8px" }}>
                    <span style={{ background: m.profit >= 0 ? "#d1fae5" : "#fee2e2", color: m.profit >= 0 ? "#15803d" : "#b91c1c", padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 700, fontFamily: "system-ui" }}>
                      {m.profit >= 0 ? "+" : ""}¥{m.profit.toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </span>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="5" style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 13 }}>No monthly data available yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 4. SMARTER PRODUCT PERFORMANCE TABLE */}
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 14 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", margin: 0 }}>Product-wise Analytics</h3>
            <p style={{ fontSize: 13, color: "#94a3b8", margin: "4px 0 0" }}>Detailed breakdown of sales, profit, and loss by item</p>
          </div>
          
          {/* Month Filter Dropdown */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f8fafc", padding: "6px 12px", borderRadius: 10, border: "1px solid #e2e8f0" }}>
            <span style={{ color: "#64748b" }}>{Icons.Filter}</span>
            <select value={selectedMonth} onChange={handleMonthChange} style={{ background: "transparent", border: "none", outline: "none", fontSize: 13, fontWeight: 600, color: "#0f172a", cursor: "pointer" }}>
              <option value="All Time">All Time</option>
              {availableMonths.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        <div className="table-responsive" style={{ minHeight: "300px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "700px" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                {["Product Name", "Sold Qty", "Total Revenue", "Loss Qty", "Loss Amount", "Net Profit"].map((h) => (
                  <th key={h} style={{ textAlign: "left", fontSize: 11, color: "#64748b", fontWeight: 600, padding: "0 8px 12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedProducts.length > 0 ? paginatedProducts.map((p, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f8fafc", transition: "background 0.2s" }} onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "16px 8px", fontSize: 13, color: "#0f172a", fontWeight: 600 }}>{p.name}</td>
                  <td style={{ padding: "16px 8px", fontSize: 13, color: "#64748b", fontWeight: 600 }}>{p.soldQty}</td>
                  <td style={{ padding: "16px 8px", fontSize: 13, color: "#6366f1", fontWeight: 700 }}>
                    ¥{p.revenue.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </td>
                  <td style={{ padding: "16px 8px", fontSize: 13, color: p.lossQty > 0 ? "#ef4444" : "#94a3b8", fontWeight: 600 }}>{p.lossQty}</td>
                  <td style={{ padding: "16px 8px", fontSize: 13, color: p.lossAmount > 0 ? "#ef4444" : "#94a3b8", fontWeight: 600 }}>
                    {p.lossAmount > 0 ? `-¥${p.lossAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}` : "-"}
                  </td>
                  <td style={{ padding: "16px 8px" }}>
                    <span style={{ background: p.profit >= 0 ? "#d1fae5" : "#fee2e2", color: p.profit >= 0 ? "#15803d" : "#b91c1c", padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 700, fontFamily: "system-ui" }}>
                      {p.profit >= 0 ? "+" : ""}¥{p.profit.toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </span>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 13 }}>No product performance data available for this period.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, paddingTop: 20, borderTop: "1px solid #f1f5f9" }}>
            <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>
              Showing Page <strong style={{ color: "#0f172a" }}>{Math.min(currentPage, totalPages)}</strong> of <strong style={{ color: "#0f172a" }}>{totalPages}</strong>
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                disabled={currentPage === 1}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 10, border: "1px solid #e2e8f0", background: currentPage === 1 ? "#f8fafc" : "#FFFFFF", color: currentPage === 1 ? "#cbd5e1" : "#0f172a", cursor: currentPage === 1 ? "not-allowed" : "pointer", transition: "all 0.2s" }}
              >
                {Icons.ChevronLeft}
              </button>
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                disabled={currentPage >= totalPages}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 10, border: "1px solid #e2e8f0", background: currentPage >= totalPages ? "#f8fafc" : "#FFFFFF", color: currentPage >= totalPages ? "#cbd5e1" : "#0f172a", cursor: currentPage >= totalPages ? "not-allowed" : "pointer", transition: "all 0.2s" }}
              >
                {Icons.ChevronRight}
              </button>
            </div>
          </div>
        )}
      </div>
      
    </div>
  );
}